import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Select, DatePicker, Button, Table, Tag, Spin, Empty, Alert, Segmented, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import api from '../../api/axios';
import { getBranches } from '../../api/branches';
import BranchSelect from '../common/BranchSelect';
import useAuthStore from '../../store/authStore';
import { numberSorter, textSorter, dateSorter } from '../../utils/tableSorters';

dayjs.extend(isoWeek);

/**
 * Haftalık derslik doluluğu — "yeni öğrenciyi hangi güne yazayım".
 *
 * Kullanım amacı (Eren Bey, 2026-08-14): bir günü tamamen doldurup diğerini
 * boş bırakmak yerine yükü dengeli dağıtmak ve her derste birkaç kişilik
 * boşluk bırakmak — telafi/acil durumda müdahale edebilmek için.
 *
 * YALNIZCA NORMAL KAYITLAR SAYILIR. Deneme ve telafi tek seferliktir, dersin
 * kalıcı yükünü göstermez. Ama o hafta konmuş telafi/deneme hücrede küçük bir
 * "+N" olarak görünüyor: boşluk bırakmanın amacı zaten telafi yerleştirebilmek,
 * üç telafi konmuş bir ders "7/10" görünüp de boş sanılmamalı.
 */

const GUN_ADLARI = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

// Doluluk oranına göre renk. Eşikler kullanıcının hedefine göre: ~%70
// (10 kapasitede 7) ideal, üstü sıkışık, altı tercih edilir.
const doluluKRengi = (dolu, kapasite) => {
  if (!kapasite) return undefined;
  const oran = dolu / kapasite;
  if (oran >= 1) return '#ff4d4f';
  if (oran >= 0.8) return '#fa8c16';
  if (oran >= 0.6) return '#faad14';
  return '#52c41a';
};

const ClassroomLoadReportTab = () => {
  const { user } = useAuthStore();
  const [branchId, setBranchId] = useState(user?.branch_id ?? null);
  // BranchSelect şubeleri KENDİ ÇEKMİYOR, `branches` prop'u bekliyor. İlk
  // sürümde geçilmemişti: süper admin/developer admin'in `branch_id`'si null
  // olduğu için kutu hem boş görünüyor hem de açılır liste boş geliyordu.
  const [branches, setBranches] = useState([]);
  const [hafta, setHafta] = useState(dayjs().startOf('isoWeek'));
  const [veri, setVeri] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hata, setHata] = useState(null);
  // Afişte kullanılan oran hafta içi 14:30 hariç isteniyor; rapor genel bir
  // araç olduğu için ham oran da duruyor, varsayılan o.
  const [dolulukHaric, setDolulukHaric] = useState(false);

  const [derslikFiltre, setDerslikFiltre] = useState(null);
  const [siralama, setSiralama] = useState('bosluk');

  const getir = useCallback(async () => {
    setLoading(true);
    setHata(null);
    try {
      const { data } = await api.get('/reports/classroom-load', {
        params: { branch_id: branchId || undefined, date: hafta.format('YYYY-MM-DD') },
      });
      setVeri(data);
    } catch (e) {
      setHata(e?.response?.status === 403
        ? 'Bu raporu görüntüleme yetkiniz yok.'
        : 'Rapor yüklenemedi.');
      setVeri(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, hafta]);

  useEffect(() => { getir(); }, [getir]);

  // Şube listesi + ilk açılışta bir şube seç. Şubesiz bir kullanıcıda
  // (süper admin) seçim yapılmazsa rapor tüm şubelerin dersliklerini bir
  // arada gösteriyor; doluluk karşılaştırması şube içinde anlamlı olduğu
  // için tek şubeyle başlamak doğru varsayılan.
  useEffect(() => {
    getBranches()
      .then(res => {
        const liste = res.data?.data || res.data || [];
        setBranches(liste);
        setBranchId(onceki => onceki ?? liste[0]?.id ?? null);
      })
      .catch(() => {});
  }, []);

  const derslikler = useMemo(() => veri?.classrooms ?? [], [veri]);

  const gosterilenDerslikler = useMemo(
    () => (derslikFiltre ? derslikler.filter(c => c.id === derslikFiltre) : derslikler),
    [derslikler, derslikFiltre],
  );

  // Öneriler zaten sunucudan "en boş önce" sıralı geliyor; buradaki sıralama
  // kullanıcının başka bir ölçüte geçmek istemesi için.
  const oneriler = useMemo(() => {
    const liste = (veri?.suggestions ?? []).filter(o => !derslikFiltre || o.classroom_id === derslikFiltre);

    const kopya = [...liste];
    if (siralama === 'gun') {
      kopya.sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
    } else if (siralama === 'oran') {
      // Kapasiteye GÖRE boşluk: 3/13 ile 3/10 aynı değil. Farklı kapasiteli
      // derslikler birlikte listelendiğinde bu ölçüt daha adil.
      kopya.sort((a, b) => (a.regular / a.capacity) - (b.regular / b.capacity));
    }
    return kopya;
  }, [veri, derslikFiltre, siralama]);

  /** Bir dersliğin belirli bir gündeki toplam normal kaydı. */
  const derslikGunToplami = (c, tarih) =>
    (c.slots ?? []).reduce((t, s) => t + (s.days?.[tarih]?.regular ?? 0), 0);

  // En alttaki özet tablo: satır = gün, sütun = her derslik + toplam.
  const ozetSatirlari = useMemo(() => {
    if (!veri) return [];
    return veri.days.map((tarih, i) => {
      const satir = { key: tarih, tarih, gun: GUN_ADLARI[i] };
      let toplam = 0;
      gosterilenDerslikler.forEach(c => {
        const t = derslikGunToplami(c, tarih);
        satir[`c${c.id}`] = t;
        toplam += t;
      });
      satir.toplam = toplam;
      satir.tekil = veri.unique_students?.by_day?.[tarih] ?? null;
      return satir;
    });
  }, [veri, gosterilenDerslikler]);

  const haftaToplami = ozetSatirlari.reduce((t, r) => t + r.toplam, 0);

  if (hata) return <Alert type="warning" showIcon message={hata} />;

  // Tüm veri bellekte (sunucu sayfalaması yok), o yüzden istemci sıralaması
  // doğru sonuç veriyor — bkz. utils/tableSorters kullanım sınırı.
  const kolonlar = [
    {
      title: 'Saat',
      dataIndex: 'start_time',
      width: 130,
      sorter: textSorter('start_time'),
      defaultSortOrder: 'ascend',
      render: (_, r) => <span className="font-medium">{r.start_time} — {r.end_time}</span>,
    },
    ...(veri?.days ?? []).map((tarih, i) => ({
      title: (
        <div className="text-center leading-tight">
          <div>{GUN_ADLARI[i]}</div>
          <div className="text-xs font-normal text-gray-400">{dayjs(tarih).format('DD.MM')}</div>
        </div>
      ),
      dataIndex: ['days', tarih],
      align: 'center',
      // O güne göre sırala: "bu gün hangi saatte en boş" sorusunun cevabı.
      // Dersi olmayan hücre (null) sona düşsün diye çok büyük sayı veriliyor.
      sorter: (a, b) => (a.days?.[tarih]?.regular ?? 9999) - (b.days?.[tarih]?.regular ?? 9999),
      render: (hucre, r) => {
        // null = o gün o saatte ders yok. Boş bırakmak doğru: oraya kayıt
        // açılamıyor, "0/10" yazmak yanlış yönlendirirdi.
        if (!hucre) return <span className="text-gray-300">—</span>;

        const renk = doluluKRengi(hucre.regular, r.capacity);
        return (
          <div>
            <span style={{ color: renk, fontWeight: 600 }}>
              {hucre.regular}/{r.capacity}
            </span>
            {hucre.extra > 0 && (
              <Tooltip title={`Bu hafta ayrıca ${hucre.extra} telafi/deneme öğrencisi var — sayıya dahil değil`}>
                <span className="ml-1 text-xs text-purple-500">+{hucre.extra}</span>
              </Tooltip>
            )}
          </div>
        );
      },
    })),
  ];

  const oneriKolonlari = [
    {
      title: 'Gün',
      dataIndex: 'date',
      sorter: dateSorter('date'),
      render: (t, r) => (
        <span>
          <span className="font-medium">{GUN_ADLARI[r.day_of_week - 1]}</span>
          <span className="text-gray-400 text-xs ml-1">{dayjs(t).format('DD.MM')}</span>
        </span>
      ),
    },
    {
      title: 'Saat',
      dataIndex: 'start_time',
      sorter: textSorter('start_time'),
      render: (s, r) => `${s} — ${r.end_time}`,
    },
    { title: 'Derslik', dataIndex: 'classroom_name', sorter: textSorter('classroom_name') },
    {
      title: 'Kayıtlı',
      dataIndex: 'regular',
      align: 'center',
      sorter: numberSorter('regular'),
      render: (v, r) => (
        <span style={{ color: doluluKRengi(v, r.capacity), fontWeight: 600 }}>{v}/{r.capacity}</span>
      ),
    },
    {
      title: 'Boş',
      dataIndex: 'free',
      align: 'center',
      sorter: numberSorter('free'),
      render: (v, r) => (
        <>
          <Tag color={v >= 4 ? 'green' : v >= 2 ? 'gold' : 'orange'}>{v} kişilik</Tag>
          {r.extra > 0 && (
            <Tooltip title="Bu hafta bu derse konmuş telafi/deneme sayısı">
              <span className="text-xs text-purple-500">+{r.extra} telafi</span>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <Card size="small" className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <BranchSelect branches={branches} value={branchId} onChange={setBranchId} style={{ width: 200 }} />

          <div className="flex items-center gap-1">
            <Button icon={<LeftOutlined />} onClick={() => setHafta(h => h.subtract(1, 'week'))} />
            <DatePicker
              picker="week"
              value={hafta}
              allowClear={false}
              onChange={(d) => d && setHafta(d.startOf('isoWeek'))}
              format={(d) => `${d.startOf('isoWeek').format('DD.MM')} – ${d.endOf('isoWeek').format('DD.MM.YYYY')}`}
              style={{ width: 190 }}
            />
            <Button icon={<RightOutlined />} onClick={() => setHafta(h => h.add(1, 'week'))} />
            <Button onClick={() => setHafta(dayjs().startOf('isoWeek'))}>Bu hafta</Button>
          </div>

          <Select
            placeholder="Tüm derslikler"
            allowClear
            value={derslikFiltre}
            onChange={setDerslikFiltre}
            style={{ width: 230 }}
            options={derslikler.map(c => ({ value: c.id, label: c.name }))}
          />

          <Button icon={<ReloadOutlined />} onClick={getir} loading={loading}>Yenile</Button>
        </div>

        <div className="mt-2 text-xs text-gray-500">
          Sayılar <strong>yalnızca normal (her hafta tekrar eden) kayıtları</strong> gösterir.
          Telafi ve deneme öğrencileri sayıya dahil değildir; o hafta bir derse konmuşlarsa
          hücrede mor <span className="text-purple-500">+N</span> olarak belirtilir.
          <span className="text-gray-300"> —</span> işareti o gün o saatte ders olmadığı anlamına gelir.
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Spin /></div>
      ) : gosterilenDerslikler.length === 0 ? (
        <Empty description="Bu şubede uygun derslik bulunamadı." />
      ) : (
        <>
          <Card
            size="small"
            className="mb-4"
            title="Yerleştirme önerisi — en az kayıtlı dersler önce"
            extra={
              <Segmented
                size="small"
                value={siralama}
                onChange={setSiralama}
                options={[
                  { label: 'En boş', value: 'bosluk' },
                  { label: 'Doluluk oranı', value: 'oran' },
                  { label: 'Güne göre', value: 'gun' },
                ]}
              />
            }
          >
            {oneriler.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Bu hafta boş yer görünmüyor." />
            ) : (
              <Table
                size="small"
                rowKey={(r) => `${r.classroom_id}-${r.date}-${r.start_time}`}
                columns={oneriKolonlari}
                dataSource={oneriler}
                pagination={{ pageSize: 10, showSizeChanger: false, size: 'small' }}
              />
            )}
          </Card>

          {gosterilenDerslikler.map(c => (
            <Card key={c.id} size="small" className="mb-4" title={`${c.name} — kapasite ${c.capacity}`}>
              {c.slots.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Bu hafta bu derslikte ders yok." />
              ) : (
                <Table
                  size="small"
                  rowKey="start_time"
                  columns={kolonlar}
                  dataSource={c.slots.map(s => ({ ...s, capacity: c.capacity }))}
                  pagination={false}
                  summary={() => (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                        <Table.Summary.Cell index={0}>Gün toplamı</Table.Summary.Cell>
                        {veri.days.map((tarih, i) => (
                          <Table.Summary.Cell key={tarih} index={i + 1} align="center">
                            {derslikGunToplami(c, tarih) || <span className="text-gray-300">—</span>}
                          </Table.Summary.Cell>
                        ))}
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              )}
            </Card>
          ))}

          {/* KONTENJAN DOLULUK ORANI — kurum bunu afiş/sosyal medyada
              kullanıyor ("%76 kontenjanımız dolu").

              Hafta içi 14:30 dersleri okul saatine denk geldiği için fiilen
              kullanılmıyor ve oranı aşağı çekiyor; anahtar onları paydadan
              çıkarıyor. İki rakam da sunucudan tek istekte geliyor, geçiş
              yaparken yeniden yüklenmiyor.

              Deneme/telafi öğrencisi PAYA GİRMİYOR: kontenjan kalıcı koltuk
              demek. Sayı dışarıya verildiği için fazla göstermek yanlış
              beyan olur. */}
          {veri.occupancy && (
            <Card size="small" className="mb-4" title="Kontenjan doluluk oranı">
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <div className="text-4xl font-bold" style={{ color: '#fa541c', lineHeight: 1.1 }}>
                    %{(dolulukHaric ? veri.occupancy.haric_hafta_ici_1430 : veri.occupancy.tumu).oran}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {(dolulukHaric ? veri.occupancy.haric_hafta_ici_1430 : veri.occupancy.tumu).dolu}
                    {' / '}
                    {(dolulukHaric ? veri.occupancy.haric_hafta_ici_1430 : veri.occupancy.tumu).kapasite} koltuk
                    {' · '}
                    {(dolulukHaric ? veri.occupancy.haric_hafta_ici_1430 : veri.occupancy.tumu).slot} ders saati
                  </div>
                </div>

                <Segmented
                  size="small"
                  value={dolulukHaric ? 'haric' : 'tumu'}
                  onChange={(v) => setDolulukHaric(v === 'haric')}
                  options={[
                    { label: 'Tüm dersler', value: 'tumu' },
                    { label: 'Hafta içi 14:30 hariç', value: 'haric' },
                  ]}
                />
              </div>

              <div className="text-xs text-gray-400 mt-3">
                Bu haftanın açılmış ders saatleri üzerinden; telafi ve deneme
                öğrencileri sayıya dahil değil.
              </div>
            </Card>
          )}

          {/* ÖZET — kullanıcının istediği üçüncü tablo: gün gün, derslik
              derslik ve haftanın tamamı. */}
          <Card size="small" title="Haftalık toplam">
            <Table
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={ozetSatirlari}
              columns={[
                {
                  title: 'Gün',
                  dataIndex: 'gun',
                  render: (g, r) => (
                    <span>
                      <span className="font-medium">{g}</span>
                      <span className="text-gray-400 text-xs ml-1">{dayjs(r.tarih).format('DD.MM')}</span>
                    </span>
                  ),
                },
                ...gosterilenDerslikler.map(c => ({
                  title: c.name,
                  dataIndex: `c${c.id}`,
                  align: 'center',
                  sorter: numberSorter(`c${c.id}`),
                  render: (v) => v || <span className="text-gray-300">—</span>,
                })),
                {
                  title: 'Toplam ders kaydı',
                  dataIndex: 'toplam',
                  align: 'center',
                  sorter: numberSorter('toplam'),
                  render: (v) => <strong>{v}</strong>,
                },
                {
                  title: 'Farklı öğrenci',
                  dataIndex: 'tekil',
                  align: 'center',
                  sorter: numberSorter('tekil'),
                  render: (v) => (v == null ? '—' : v),
                },
              ]}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0}>Hafta toplamı</Table.Summary.Cell>
                    {gosterilenDerslikler.map((c, i) => (
                      <Table.Summary.Cell key={c.id} index={i + 1} align="center">
                        {veri.days.reduce((t, tarih) => t + derslikGunToplami(c, tarih), 0)}
                      </Table.Summary.Cell>
                    ))}
                    <Table.Summary.Cell index={gosterilenDerslikler.length + 1} align="center">
                      <strong>{haftaToplami}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={gosterilenDerslikler.length + 2} align="center">
                      <strong>{veri.unique_students?.week ?? '—'}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            {/* Bu ayrımı yazmazsak "210 öğrencimiz var ama tabloda 380 yazıyor"
                sorusu geliyor ve rapora güven kayboluyor. */}
            <div className="mt-2 text-xs text-gray-500">
              <strong>Toplam ders kaydı</strong>, o gün açılan derslerdeki kayıt sayısıdır — haftada
              iki gün gelen bir öğrenci iki günde de sayılır, dolayısıyla hafta toplamı
              öğrenci sayısından büyüktür. <strong>Farklı öğrenci</strong> sütunu ise o gün
              (ve en altta o hafta) derse gelen kaç ayrı öğrenci olduğunu gösterir.
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default ClassroomLoadReportTab;
